import { useState } from "react";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import deleteGroups from "../../pages/api/groups/delete-group";
import deleteUser from "../../pages/api/users/delete-user";
import deleteApp from "../../pages/api/apps/delete-app";
import deleteVersions from "../../pages/api/versions/delete-versions";
import { green, red } from "@mui/material/colors";
import { User, Group } from "../users/users-types";
import { Application, Version } from "../apps-components/apps-types";
import { Clear, Done } from "@mui/icons-material";

export default function DeleteElementFromTable(props: {
  open: boolean;
  setOpen: any;
  entity: string;
  id: number;
  name: string;
  appID?: number;
  appArray?: Application[];
  userArray?: User[];
  groupArray?: Group[];
  versionArray?: Version[];
}) {
  const { t } = useTranslation();

  const [openConfirm, setOpenConfirm] = useState(false);
  const [status, setStatus] = useState<number>(0);

  const handleConfirmOpen = () => {
    props.setOpen(false);
    setOpenConfirm(true);
  };

  const handleConfirmClose = () => {
    setOpenConfirm(false);
    setStatus(0);
  };

  const getStatus = async () => {
    let statusReq = 0;
    switch (props.entity) {
      case "apps":
        statusReq = await deleteApp(props.id);
        break;

      case "versions":
        if (props.appID !== undefined) {
          statusReq = await deleteVersions(props.id, props.appID);
        }
        break;

      case "user":
        statusReq = await deleteUser(props.id);
        break;

      case "group":
        statusReq = await deleteGroups(props.id);
        break;

      default:
        break;
    }
    setStatus(statusReq);
  };

  const handleDelete = () => {
    getStatus();
    handleConfirmOpen();
  };

  return (
    <div>
      <Dialog
        fullWidth
        open={props.open}
        onClose={() => props.setOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {t("Delete Confirmation")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {!props.appArray
              ? t("Do you want to delete ") +
                t(props.entity) +
                " " +
                props.name +
                "?"
              : t("Do you want to delete ") +
                props.name +
                t(" and all the related versions?")}
            {props.entity === "group" &&
              " " +
                t(
                  "This operation will also delete all the applications belonging to the group and it is irreversible."
                )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => props.setOpen(false)}>{t("Close")}</Button>
          <Button onClick={handleDelete} autoFocus>
            {t("Confirm")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog fullWidth open={openConfirm}>
        <DialogTitle />
        <DialogContent>
          {status === 0 ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
              }}
            >
              <Stack
                spacing={2}
                alignItems="center"
                justifyContent="center"
                height="100%x"
              >
                <CircularProgress size={80} thickness={3} />
                <Typography variant="h5" textAlign="center">
                  {t("Deleting") + "..."}
                </Typography>
              </Stack>
            </Box>
          ) : status >= 200 && status < 300 ? (
            <Stack alignItems="center" justifyContent="center" spacing={2}>
              <Box display="flex" alignItems="center" justifyContent="center">
                <Avatar sx={{ bgcolor: green[500], width: 80, height: 80 }}>
                  <Done sx={{ width: 50, height: 50 }} />
                </Avatar>
              </Box>
              <Typography variant="h4">{t("Successfully deleted!")}</Typography>
            </Stack>
          ) : (
            status >= 400 &&
            status <= 500 && (
              <Box display="flex" alignItems="center" justifyContent="center">
                <Stack justifyContent="center" alignItems="center" spacing={2}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Avatar sx={{ bgcolor: red[500], width: 80, height: 80 }}>
                      <Clear sx={{ width: 50, height: 50 }} />
                    </Avatar>
                  </Box>
                  <Stack>
                    <Typography variant="h4" textAlign="center">
                      {t("Deletion failed")}
                    </Typography>
                    <Typography variant="body1" textAlign="center">
                      {t("Retry.")}
                    </Typography>
                  </Stack>
                </Stack>
              </Box>
            )
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleConfirmClose()}>{t("Close")}</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
